// Capture Meta Lead Ads attribution params from the URL and persist
// across the seller's session. Used by Phase 4 of the Meta→GHL plan to
// correlate a takeover-completion back to the Meta lead that drove it.
//
// Meta substitutes templated macros in the lead-ad CTA destination URL,
// so a seller landing here from an ad gets URL params like:
//
//   sellyourlinkedin.com/?lead_id={{lead.leadgen_id}}
//                    &form_id={{form.id}}
//                    &page_id={{page.id}}
//                    &ad_id={{ad.id}}
//                    &adset_id={{adset.id}}
//                    &campaign_id={{campaign.id}}
//                    &ad_account_id={{ad_account.id}}
//                    &source=meta_lead_ad
//
// SUBSTITUTES ONLY THE ONES META ACTUALLY SUPPORTS — and it does not
// announce the ones it does not. `docs/planning/meta-ghl-integration-plan.md`
// (§4, and its own §8 risk note) assumed `{{form_id}}` was supported and left
// "exact macros to confirm during Meta app setup" as an open item. It was
// never confirmed. Meta's published dynamic-URL parameter set names only
// campaign / adset / ad ids and names, `{{placement}}` and
// `{{site_source_name}}` — no lead, form or page macro. An unsupported macro
// is NOT dropped: it is passed through to the landing page verbatim. So
// `?form_id={{form_id}}` arrives as the literal seven-brace-wrapped string,
// and until 2026-09-09 we stored it: `meta_form_id = '{{form_id}}'` on 1,565
// of 1,932 sessions in 14 days, passing every `IS NOT NULL` check while
// naming no form.
//
// Fixing the ad URL is an Ads Manager change, not a code change, and it is
// NOT sufficient on its own — a regression there, or any other template that
// leaks, refills the column with the same garbage. So every value now goes
// through the ingest boundary in `attributionValue.js`, which refuses a
// placeholder and records the rejection by name in `meta_rejected_params`.
//
// Organic / direct-traffic sessions have no params at all, which is fine —
// the completion webhook falls back to email/phone matching when leadgen_id
// is missing.
//
// We persist into sessionStorage so the values survive page reloads /
// step navigations within the wizard. SessionStorage clears when the
// browser tab closes, which matches the lifecycle of one takeover.

import { normaliseAttributionValue, REASONS } from './attributionValue';

const STORAGE_KEY = 'takeover.metaAttribution';

// Recognised aliases for the leadgen id macro. Meta isn't consistent —
// some accounts substitute {leadgen_id}, some {lead_id}, some
// {ad.lead_id}. Accept all common variants on the way in but normalise
// to `meta_leadgen_id` on the way out.
const LEAD_ID_PARAMS = ['lead_id', 'leadgen_id', 'lid'];
const ADSET_ID_PARAMS = ['adset_id', 'adgroup_id'];

// Meta ids are numeric strings; `source` is a short slug. Anything outside
// this charset is a mangled link, not an id.
const META_VALUE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

// Read the first param from `names` that survives the ingest boundary.
// Returns `{ value, rejected }` where `rejected` lists the param names that
// carried an unsubstituted template, so the rejection stays countable rather
// than looking identical to the seller never having had the param.
function firstUsable(searchParams, names) {
  const rejected = [];
  for (const name of names) {
    const { value, reason } = normaliseAttributionValue(
      searchParams.get(name), META_VALUE_PATTERN,
    );
    if (value) return { value, rejected };
    if (reason === REASONS.PLACEHOLDER || reason === REASONS.INVALID) rejected.push(name);
  }
  return { value: null, rejected };
}

/**
 * Read URL query params and persist any Meta attribution fields to
 * sessionStorage. Safe to call repeatedly — later calls only OVERWRITE
 * when a fresh URL carries a leadgen_id, so a deep-link with attribution
 * always wins over stale storage.
 *
 * Idempotent within a single page load: calling on every render is a
 * no-op after the first hit.
 */
export function captureMetaAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const rejected = [];
    const take = (field, names) => {
      const r = firstUsable(params, names);
      rejected.push(...r.rejected);
      return [field, r.value];
    };
    const captured = Object.fromEntries([
      take('meta_leadgen_id',    LEAD_ID_PARAMS),
      take('meta_page_id',       ['page_id']),
      take('meta_form_id',       ['form_id']),
      take('meta_ad_id',         ['ad_id']),
      take('meta_adset_id',      ADSET_ID_PARAMS),
      take('meta_campaign_id',   ['campaign_id']),
      take('meta_ad_account_id', ['ad_account_id']),
      take('meta_source',        ['source']),
    ]);
    // Strip nulls so the persisted object only contains real values.
    const trimmed = Object.fromEntries(
      Object.entries(captured).filter(([, v]) => v !== null && v !== ''),
    );
    // Name the params that arrived as an unsubstituted template. Without this
    // the rejection is invisible: the column is NULL either way, and a broken
    // ad URL looks exactly like organic traffic. Persisted alongside the
    // values and stored in `takeover_sessions.meta_rejected_params`.
    if (rejected.length > 0) {
      // Sorted so the stored string is stable regardless of param order in
      // the URL — it gets grouped and counted, so it must not vary.
      trimmed.meta_rejected_params = [...new Set(rejected)].sort().join(',').slice(0, 200);
    }

    // Only write if we actually captured something OR storage is empty.
    // This prevents an in-app navigation that drops query params from
    // clearing the original attribution.
    if (Object.keys(trimmed).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      return trimmed;
    }
    return readStoredAttribution();
  } catch {
    // sessionStorage can throw in private browsing or when disabled.
    // Attribution is best-effort, never block the takeover flow.
    return null;
  }
}

/**
 * Return the persisted Meta attribution dict, or null if none captured.
 */
export function readStoredAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Clear stored attribution. Currently unused — we keep attribution
 * for the lifetime of the tab — but exposed in case a future step
 * needs to reset it (e.g., the seller restarts the wizard from
 * scratch after declining consent).
 */
export function clearStoredAttribution() {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
